import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, Package, Sparkles, Tag } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import PackBuilder from "../components/PackBuilder";
import RecentlyViewed from "../components/RecentlyViewed";
import { SHOP_CATEGORIES, ECOM_CATEGORY_IDS } from "../lib/brand";
import {
  PACK_SIZES,
  PACK_DISCOUNTS,
  calcPackBase,
  calcPackPrice,
  SHIPPING_FEE,
  SHIPPING_LABEL,
} from "../lib/pricing";
import { usePageMeta } from "../hooks/usePageMeta";
import { useStructuredData } from "../hooks/useStructuredData";
import { PAGE_SEO } from "../lib/seo";

const SITE_URL = "https://justdessertme.co.nz";

const UNIT_PRICES = {
  "cookie-pies": 6.50,
  "loaded-cookies": 4.90,
};

const PACK_META = {
  "cookie-pies": {
    label: "Cookie Pies",
    title: "Build your Cookie Pies Pack",
    accent: "from-[#ff1a8c] to-[#ff5fc8]",
  },
  "loaded-cookies": {
    label: "Loaded Cookies",
    title: "Build your Loaded Cookies Pack",
    accent: "from-[#00b4ff] to-[#8ee0ff]",
  },
};

const Shop = () => {
  usePageMeta(PAGE_SEO.shop);
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get("category") || "all";
  const [category, setCategory] = useState(initial);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [packCategory, setPackCategory] = useState(null);
  const [packInitialSize, setPackInitialSize] = useState(4);

  const openPackBuilder = (catId, size) => {
    setPackCategory(catId);
    setPackInitialSize(size);
  };

  useEffect(() => {
    api.get("/products").then(({ data }) => {
      setProducts(data.filter((p) => ECOM_CATEGORY_IDS.has(p.category)));
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (category === "all") {
      searchParams.delete("category");
    } else {
      searchParams.set("category", category);
    }
    setSearchParams(searchParams, { replace: true });
  }, [category]);

  const filtered = useMemo(() => {
    if (category === "all") return products;
    return products.filter((p) => p.category === category);
  }, [products, category]);

  const productsForPack = useMemo(() => {
    if (!packCategory) return [];
    return products.filter((p) => p.category === packCategory);
  }, [products, packCategory]);

  // Build an ItemList schema of online-orderable products for SEO rich results.
  const productListSchema = useMemo(() => {
    const onlineProducts = products.filter((p) => !p.in_store_only);
    if (!onlineProducts.length) return null;
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Just Dessert Me — Online Shop",
      url: `${SITE_URL}/shop`,
      numberOfItems: onlineProducts.length,
      itemListElement: onlineProducts.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Product",
          name: p.name,
          description: p.description || `${p.name} — handcrafted in Hamilton by Just Dessert Me, delivered NZ-wide.`,
          image: p.image,
          sku: p.id,
          brand: { "@type": "Brand", name: "Just Dessert Me" },
          category: p.category,
          offers: {
            "@type": "Offer",
            url: `${SITE_URL}/shop?category=${p.category}`,
            priceCurrency: "NZD",
            price: p.price.toFixed(2),
            availability: "https://schema.org/InStock",
            itemCondition: "https://schema.org/NewCondition",
            seller: { "@type": "Organization", name: "Just Dessert Me" },
            shippingDetails: {
              "@type": "OfferShippingDetails",
              shippingRate: {
                "@type": "MonetaryAmount",
                value: SHIPPING_FEE.toFixed(2),
                currency: "NZD",
              },
              shippingDestination: {
                "@type": "DefinedRegion",
                addressCountry: "NZ",
              },
            },
          },
        },
      })),
    };
  }, [products]);
  useStructuredData("shop-itemlist", productListSchema);

  return (
    <main data-testid="page-shop" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="mb-12 text-center fade-up">
        <p className="text-xs uppercase tracking-[0.3em] text-[#00b4ff] mb-3">Cookie Pies Hamilton · Shipped NZ-wide</p>
        <h1 className="font-script text-6xl sm:text-7xl neon-gradient-text">Shop</h1>
        <p className="text-gray-400 max-w-2xl mx-auto mt-4">
          Order Hamilton&apos;s famous <span className="text-white">Cookie Pies</span> and{" "}
          <span className="text-white">Loaded Cookies</span> online — also available at our
          Hillcrest dessert shop. Everything else (milkshakes, pancakes, waffles, rolled ice cream) is{" "}
          <Link to="/menus" className="text-[#ff1a8c] hover:text-white underline-offset-4 underline">
            in-store only
          </Link>.
        </p>
        <div className="mt-5 inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.25em] text-[#00b4ff]">
          <Tag className="w-3.5 h-3.5" /> {SHIPPING_LABEL} · ${SHIPPING_FEE.toFixed(2)} per order
        </div>
      </div>

      {/* Pack builder CTAs */}
      <section
        data-testid="pack-builder-section"
        className="grid md:grid-cols-2 gap-6 mb-10"
      >
        {Object.entries(PACK_META).map(([catId, meta]) => (
          <div
            key={catId}
            data-testid={`pack-card-${catId}`}
            className="glass rounded-3xl border border-white/10 p-7 lg:p-9 hover:border-[#ff1a8c]/60 hover:shadow-[0_0_28px_rgba(255,26,140,0.25)] transition-all relative overflow-hidden"
          >
            <div className={`absolute -top-12 -right-12 w-44 h-44 rounded-full blur-3xl opacity-30 bg-gradient-to-br ${meta.accent}`} />
            <div className="relative">
              <span className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.3em] text-[#ff1a8c] mb-3">
                <Sparkles className="w-3.5 h-3.5" /> Build a pack · bulk discount
              </span>
              <h2 className="font-script text-4xl sm:text-5xl text-white mb-3">
                {meta.label}
              </h2>
              <p className="text-sm text-gray-400 mb-5 leading-relaxed">
                Mix and match flavours in a 4, 8, or 12 pack at ${UNIT_PRICES[catId].toFixed(2)} each.
                Save more the bigger you go.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                {PACK_SIZES.map((size) => {
                  const base = calcPackBase(size, UNIT_PRICES[catId]);
                  const price = calcPackPrice(size, UNIT_PRICES[catId]);
                  const disc = Math.round((PACK_DISCOUNTS[size] || 0) * 100);
                  return (
                    <button
                      key={size}
                      data-testid={`open-pack-${catId}-${size}`}
                      onClick={() => openPackBuilder(catId, size)}
                      className="group flex flex-col items-start gap-1 px-4 py-3 rounded-2xl border border-white/15 hover:border-[#00b4ff] hover:bg-[#00b4ff]/10 text-left transition-all"
                    >
                      <span className="flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.18em] text-gray-300">
                        <Package className="w-3.5 h-3.5 text-[#00b4ff]" /> {size}-Pack
                      </span>
                      <span className="flex items-baseline gap-2">
                        <span className="text-lg font-bold text-white">${price.toFixed(2)}</span>
                        <span className="text-xs text-gray-500 line-through">${base.toFixed(2)}</span>
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.2em] text-[#00b4ff]">
                        Save {disc}%
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Section divider */}
      <div className="flex items-center gap-4 mb-10">
        <hr className="hr-neon flex-1" />
        <p className="text-[0.7rem] uppercase tracking-[0.3em] text-gray-400 whitespace-nowrap">
          or shop individually
        </p>
        <hr className="hr-neon flex-1" />
      </div>

      {/* Recently viewed (only shown if 2+ items previously added) */}
      <RecentlyViewed products={products} />

      <div data-testid="shop-category-filters" className="flex flex-wrap items-center justify-center gap-3 mb-12">
        {SHOP_CATEGORIES.map((c) => (
          <button
            key={c.id}
            data-testid={`filter-${c.id}`}
            onClick={() => setCategory(c.id)}
            className={`px-5 py-2 rounded-full text-xs uppercase tracking-[0.2em] border transition-all ${
              category === c.id
                ? "bg-[#ff1a8c] border-[#ff1a8c] text-white shadow-[0_0_18px_rgba(255,26,140,0.6)]"
                : "border-white/15 text-gray-300 hover:border-[#00b4ff] hover:text-white"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div data-testid="shop-loading" className="flex items-center justify-center py-32 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-3" /> Loading sweetness…
        </div>
      ) : filtered.length === 0 ? (
        <div data-testid="shop-empty" className="text-center py-20 text-gray-400">
          No products in this category yet.
        </div>
      ) : (
        <div data-testid="shop-product-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {filtered.map((p) => (
            <ProductCard key={p.id} product={p} />
          ))}
        </div>
      )}

      <PackBuilder
        open={!!packCategory}
        onClose={() => setPackCategory(null)}
        title={packCategory ? PACK_META[packCategory].title : ""}
        category={packCategory || ""}
        categoryLabel={packCategory ? PACK_META[packCategory].label : ""}
        products={productsForPack}
        unitPrice={packCategory ? UNIT_PRICES[packCategory] : 0}
        initialSize={packInitialSize}
      />
    </main>
  );
};

export default Shop;
